import React, { useEffect, useState } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip,
    Box, CircularProgress, Alert, Avatar, Divider, Collapse,
    IconButton, Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WifiIcon from '@mui/icons-material/Wifi';

const API_URL = process.env.REACT_APP_API_URL;

const GestionUsuarios = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedUser, setExpandedUser] = useState(null);

    useEffect(() => {
        const fetchUsuarios = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: 'include',
                });
                if (!response.ok) {
                    const data = await response.json();
                    throw new Error(data.message || 'Error al obtener usuarios');
                }
                const data = await response.json();
                setUsuarios(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchUsuarios();
    }, []);

    const formatFecha = (fecha) => {
        if (!fecha) return 'Nunca';
        return new Date(fecha).toLocaleString('es-CO', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatHora = (fecha) => {
        return new Date(fecha).toLocaleTimeString('es-CO', {
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    const tiempoRelativo = (fecha) => {
        if (!fecha) return null;
        const diff = Date.now() - new Date(fecha).getTime();
        const minutos = Math.floor(diff / 60000);
        if (minutos < 60) return `hace ${minutos} min`;
        const horas = Math.floor(minutos / 60);
        if (horas < 24) return `hace ${horas} h`;
        const dias = Math.floor(horas / 24);
        return `hace ${dias} día${dias !== 1 ? 's' : ''}`;
    };

    const getAccesosHoy = (loginHistory = []) => {
        const hoy = new Date();
        return loginHistory.filter(entry => {
            const d = new Date(entry.date);
            return d.getFullYear() === hoy.getFullYear() &&
                   d.getMonth() === hoy.getMonth() &&
                   d.getDate() === hoy.getDate();
        }).sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
            <Alert severity="error">{error}</Alert>
        </Container>
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Usuarios registrados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} en el sistema
            </Typography>

            <TableContainer component={Paper} elevation={3}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#1565c0' }}>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold', width: 40 }} />
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Usuario</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Rol</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Último acceso</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Accesos hoy</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Registro</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usuarios.map((u) => {
                            const accesosHoy = getAccesosHoy(u.loginHistory);
                            const isExpanded = expandedUser === u._id;
                            return (
                                <React.Fragment key={u._id}>
                                    <TableRow hover>
                                        <TableCell sx={{ p: 0, pl: 1 }}>
                                            {accesosHoy.length > 0 && (
                                                <Tooltip title={isExpanded ? 'Ocultar accesos' : 'Ver accesos de hoy'}>
                                                    <IconButton size="small" onClick={() => setExpandedUser(isExpanded ? null : u._id)}>
                                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {u.avatar
                                                    ? <Avatar src={u.avatar} sx={{ width: 32, height: 32 }} />
                                                    : <Avatar sx={{ width: 32, height: 32, bgcolor: u.role === 'admin' ? '#1565c0' : '#546e7a' }}>
                                                        {u.username?.charAt(0).toUpperCase()}
                                                      </Avatar>
                                                }
                                                <Typography variant="body2" fontWeight="medium">
                                                    {u.username}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {u.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={u.role === 'admin' ? <AdminPanelSettingsIcon /> : <PersonIcon />}
                                                label={u.role === 'admin' ? 'Administrador' : 'Usuario'}
                                                color={u.role === 'admin' ? 'primary' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {formatFecha(u.lastLogin)}
                                                </Typography>
                                                {u.lastLogin && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {tiempoRelativo(u.lastLogin)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={accesosHoy.length}
                                                size="small"
                                                color={accesosHoy.length > 0 ? 'success' : 'default'}
                                                variant={accesosHoy.length > 0 ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatFecha(u.createdAt)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {/* Fila expandible con accesos de hoy */}
                                    {isExpanded && (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ p: 0, bgcolor: '#f5f5f5' }}>
                                                <Collapse in={isExpanded}>
                                                    <Box sx={{ p: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <WifiIcon fontSize="small" color="primary" />
                                                            Accesos de hoy — {u.username}
                                                        </Typography>
                                                        <Divider sx={{ mb: 1 }} />
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>#</TableCell>
                                                                    <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>Hora</TableCell>
                                                                    <TableCell sx={{ fontWeight: 'bold', color: '#555' }}>IP</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {accesosHoy.map((entry, i) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell>{accesosHoy.length - i}</TableCell>
                                                                        <TableCell>
                                                                            <Typography variant="body2" fontWeight="medium">
                                                                                {formatHora(entry.date)}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={entry.ip || 'desconocida'}
                                                                                size="small"
                                                                                variant="outlined"
                                                                                color="info"
                                                                                sx={{ fontFamily: 'monospace' }}
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    );
};

export default GestionUsuarios;

